/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React, { useMemo } from "react";

import moment from "moment";

import { Button, Label, Checkbox, Icon, Generic } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Link } from "react-router-dom";

import BookingCodeInput from "./BookingCodeInput";

import { AddButton } from "../../../components/buttons";

import {
  DispatchingTractorSelect,
  DispatchingTrailerSelect,
  CustomDriverSelect
} from "../../../components/inputs";

import NextCertificationExp from "./NextCertificationExp";
import { TableCell } from "../../../components/DataTable";

export const generateOrderDispatchingColumns = ({
  onChange,
  currentUser,
  fetchData,
  handleAssignDriver,
  handleRemoveDriver,
  OrdersSystemCodes,
  handleExport,
  handleResetToBooked,
  VehicleAvailableSystemCode,
  selected,
  onSelect,
  onSelectAll
}) => {
  const OrderBookingCtg =
    Array.isArray(OrdersSystemCodes) &&
    OrdersSystemCodes.find(s => s.CodeId === "O");
  return [
    {
      Header: ({ data }) => {
        const allSelected = useMemo(
          () =>
            selected.length &&
            data.every(trip =>
              trip.Orders.every(order =>
                selected.some(s => s === order.OrderNumber)
              )
            ),
          [data]
        );
        const someSelected = useMemo(
          () =>
            selected.length &&
            data.some(trip =>
              trip.Orders.every(order =>
                selected.some(s => s === order.OrderNumber)
              )
            ),
          [data]
        );
        return (
          <TableCell.Checkbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={() =>
              onSelectAll(
                allSelected,
                data.reduce(
                  (acc, curr) => [
                    ...acc,
                    ...curr.Orders.map(order => order.OrderNumber)
                  ],
                  []
                )
              )
            }
          />
        );
      },
      id: "RecId",
      defaultCanSort: false,
      disableSortBy: true,
      accessor: "Orders",
      Cell: ({ row }) => (
        <TableCell.Checkbox
          checked={row.original.Orders.map(
            order => order.OrderNumber
          ).every(orderNumber => selected.includes(orderNumber))}
          onChange={() => onSelect(row.original)}
          onClick={e => e.stopPropagation()}
        />
      )
    },
    {
      Header: "",
      id: "id",
      defaultCanSort: false,
      disableSortBy: true,
      Cell: ({ row, column }) => (
        <AddButton
          icon={row.original.PickupDriverRecIdRef ? "times" : "plus"}
          title={`${
            row.original.PickupDriverRecIdRef ? "Remove" : "Add"
          } Driver`}
          onClick={() =>
            row.original.PickupDriverRecIdRef
              ? handleRemoveDriver(
                  row.index,
                  column.id,
                  row.original.PickupDriverRecIdRef
                )
              : handleAssignDriver(row.index, column.id, row.original.Trip)
          }
        />
      )
    },

    {
      Header: "Start",
      accessor: "StartDate",
      id: "StartDate",
      Cell: ({ cell }) =>
        cell.value ? moment(cell.value).format("HH:mm") : "",
      sortType: (a, b) => {
        const aVal = moment(a?.original?.StartDate).format("HH:mm");
        const bVal = moment(b?.original?.StartDate).format("HH:mm");

        if (aVal > bVal) return 1;
        if (bVal > aVal) return -1;
        return 0;
      }
    },
    {
      Header: "#",
      accessor: "Trip",
      id: "Trip"
    },

    {
      Header: "Description",
      id: "Description",
      accessor: "Description",
      Cell: ({ cell }) => (
        <div>
          {String(cell.value)
            .split(" / ")
            .map(item => (
              <span>
                {item}
                <br />
              </span>
            ))}
        </div>
      )
    },
    {
      Header: "Driver",
      id: "PickupDriverRecIdRef",
      Cell: ({ row, column }) => {
        if (
          row.original.PickupDriverRecIdRef &&
          row.original.PickupDriver &&
          row.original.PickupDriver.FullName
        ) {
          // We need diff days between today and Start Date of Order,
          // exactly as on Driver Bookings tab
          let diffDays = moment().diff(row.original.StartDate, "days");
          diffDays += row.original.PickupDriver.NextCertExp > 0 ? 1 : 0;
          return (
            <div style={{ display: "flex" }}>
              <span>{row.original.PickupDriver.FullName}</span>
              {row.original.PickupDriver.NextCertExp <= 45 && (
                <Generic
                  tooltip={`This driver's certifications expire in ${row
                    .original.PickupDriver.NextCertExp + diffDays} ${
                    row.original.PickupDriver.NextCertExp + diffDays === 1
                      ? "day"
                      : "days"
                  }`}
                  tooltipPosition="right"
                >
                  <svg height="20" width="20">
                    <circle cx="10" cy="10" fill="orange" r="5" />
                  </svg>
                </Generic>
              )}
            </div>
          );
        }
        return (
          <div
            style={{
              minWidth: 150,
              display: "flex",
              alignItems: "flex-end"
            }}
          >
            <CustomDriverSelect
              disabled={!!row.original.PickupDriverRecIdRef}
              name="PickupDriverRecIdRef"
              startDate={row.original.StartDate}
              value={row.original.PickupDriverRecIdRef}
              onChange={(name, value) => {
                onChange(
                  row.index,
                  column.id,
                  ["PickupDriverRecIdRef", "DeliveryDriverRecIdRef"],
                  value
                );
              }}
            />
          </div>
        );
      }
    },
    {
      Header: "SEN.",
      accessor: "PickupDriver.SeniorityNo",
      id: "SeniorityNo",
      Cell: ({ cell }) =>
        cell.value ? String(cell.value).padStart(4, "0") : ""
    },
    {
      Header: "Booking Code",
      id: "BK",
      Cell: ({ row, column }) => (
        <BookingCodeInput
          filterBooked
          bookedTrip={row.original.BookedTrip}
          bookingCodeRecId={row.original.BookingCodeRecIdRef}
          currentUser={currentUser}
          data={{
            BookingRecIdRef: row.original.BookingRecIdRef,
            EmployeeRecIdRef: row.original.PickupDriverRecIdRef,
            BookingCodeRecIdRef: row.original.BookingCodeRecIdRef,
            Booking: row.original.Booking,
            BookedTrip: row.original.BookedTrip
          }}
          disabled={!row.original.PickupDriverRecIdRef}
          fetchData={fetchData}
          orders={row.original.Orders}
          pickupDate={row.original.StartDate}
          where={{ CodeCatgRecIdRef: OrderBookingCtg?.RecId }}
          onChange={e =>
            onChange(row.index, column.id, e.target.name, e.target.value)
          }
        />
      )
    },
    {
      Header: "Tractor",
      id: "PickupTractorRecIdRef",
      Cell: ({ row, column }) => (
        <div style={{ minWidth: 80 }}>
          {VehicleAvailableSystemCode && (
            <DispatchingTractorSelect
              name="PickupTractorRecIdRef"
              value={row.original.PickupTractorRecIdRef}
              onChange={e =>
                onChange(
                  row.index,
                  column.id,
                  ["PickupTractorRecIdRef", "DeliveryTractorRecIdRef"],
                  e.target.value
                )
              }
            />
          )}
        </div>
      )
    },
    {
      Header: "Trailer",
      id: "PickupTrailerRecIdRef",
      Cell: ({ row, column }) => (
        <div style={{ minWidth: 80 }}>
          {VehicleAvailableSystemCode && (
            <DispatchingTrailerSelect
              name="PickupTrailerRecIdRef"
              value={row.original.PickupTrailerRecIdRef}
              onChange={e =>
                onChange(
                  row.index,
                  column.id,
                  ["PickupTrailerRecIdRef", "DeliveryTrailerRecIdRef"],
                  e.target.value
                )
              }
            />
          )}
        </div>
      )
    },
    {
      Header: "Status",
      accessor: "Status.CodeDescription",
      id: "Status.CodeDescription"
    },
    {
      Header: "Order(s)",
      accessor: "Orders",
      id: "Orders",
      Cell: ({ cell }) => (
        <div>
          {cell.value.map(x => (
            <Link to={`/dispatch/orders/${x.RecId}/summary`}>
              #{x.OrderNumber}
              <br />
            </Link>
          ))}
        </div>
      )
    },
    {
      Header: " ",
      id: "Export",
      Cell: ({ row }) =>
        (row?.original?.Status?.CodeId === "BK-E" ||
          row?.original?.Status?.CodeId === "EXP") && (
          <Button
            color={row?.original?.Status?.CodeId === "EXP" ? "link" : "primary"}
            size="small"
            tooltip={
              row?.original?.Status?.CodeId === "EXP"
                ? "Reset to Booked"
                : "Export"
            }
            onClick={() =>
              row?.original?.Status?.CodeId === "BK-E"
                ? handleExport(row?.original?.Orders)
                : handleResetToBooked(row?.original?.Orders)
            }
          >
            <Icon>
              <FontAwesomeIcon
                icon={
                  row?.original?.Status?.CodeId === "EXP"
                    ? "undo"
                    : "file-export"
                }
              />
            </Icon>
          </Button>
        )
    }
  ];
};

export const generateDriverListColumns = ({
  pickupDate,
  currentUser,
  fetchData,
  selected,
  onSelect,
  BookingCodeWhere = {},
  handleUpdateDriverWeight
}) => [
  {
    Header: "",
    id: "id",
    accessor: "Employee.RecId",
    defaultCanSort: false,
    disableSortBy: true,
    Cell: ({ cell, row }) => (
      <div>
        <Label>
          <Checkbox
            checked={selected === cell.value}
            onChange={() => {
              if (row.original.NextCertExp > 0) {
                onSelect(cell.value);
              }
            }}
          />
        </Label>
      </div>
    )
  },
  {
    Header: "SEN #",
    accessor: "Employee.SeniorityNo",
    id: "SeniorityNo",
    Cell: ({ cell }) => `${cell.value}`.padStart(4, "0")
  },
  {
    Header: "ID",
    accessor: "Employee.EmployeeId",
    id: "EmployeeId"
  },
  {
    Header: "Name",
    accessor: "Employee.FullName",
    id: "FullName"
  },
  {
    Header: "Contact #1",
    accessor: "Employee.HomePhoneNo",
    id: "HomePhoneNo"
  },
  {
    Header: "Contact #2",
    accessor: "Employee.CellPhoneNo",
    id: "CellPhoneNo"
  },

  {
    Header: "Booking Code",
    id: "BK",
    Cell: ({ row }) => (
      <BookingCodeInput
        showDuplicate
        bookingCodeRecId={row.original.BookingCodeRecIdRef}
        currentUser={currentUser}
        data={row.original}
        driverRecId={row.original.EmployeeRecIdRef}
        fetchData={fetchData}
        isBookable={row.original.NextCertExp && row.original.NextCertExp > 0}
        key={row.original.RecId}
        pickupDate={pickupDate}
        where={BookingCodeWhere}
      />
    )
  },
  {
    Header: "Exams / Certs",
    accessor: "NextCertExp",
    id: "NextCertExp",
    Cell: ({ cell, row }) => (
      <NextCertificationExp cell={cell} pickupDate={pickupDate} row={row} />
    )
  },
  {
    Header: "History",
    id: "History",
    Cell: ({ row }) => (
      <Button
        as={Link}
        color="link"
        tabIndex="-1"
        to={`/dispatch/bookings/${row.original.EmployeeRecIdRef}?date=${pickupDate}`}
      >
        <Icon>
          <FontAwesomeIcon icon="history" />
        </Icon>
      </Button>
    )
  },
  {
    Header: "Driver Weight",
    accessor: "Employee.Weight",
    id: "driverWeight",
    Cell: ({ row }) => (
      <Button
        color="link"
        tabIndex="-1"
        onClick={() => handleUpdateDriverWeight(row.original)}
      >
        <Icon>
          <FontAwesomeIcon icon="weight-hanging" />
        </Icon>
      </Button>
    )
  }
];

export const DRIVER_BOOKING_LIST_COLUMNS = [
  {
    Header: "ID",
    accessor: "Employee.EmployeeId",
    id: "EmployeeId"
  },
  {
    Header: "Name",
    accessor: "Employee.FullName",
    id: "FullName"
  },
  {
    Header: "Book Date",
    accessor: "PickupDate",
    id: "PickupDate",
    Cell: ({ cell }) =>
      cell.value ? moment(cell.value).format("MM/DD/YY") : ""
  },
  {
    Header: "Booking Code",
    accessor: "BookingCode.Description",
    id: "BookingCode"
  },
  {
    Header: "Comment",
    id: "Comment",
    Cell: ({ row }) => row.original.BookedTrip || row.original.Comment
  }
];
